import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '../utils/api'
import CheckGrid from '../components/checks/CheckGrid'
import CheckDashboard from '../components/checks/CheckDashboard'
import ManageItems from '../components/checks/ManageItems'
import { FREQ_LABEL, Frequency } from '../components/checks/shared'

interface Group { id: string; name: string; frequency: Frequency; isActive: boolean }

const shiftMonth = (ym: string, n: number) => {
  const d = new Date(`${ym}-01T00:00:00Z`); d.setUTCMonth(d.getUTCMonth() + n); return d.toISOString().slice(0, 7)
}

function RecordTab({ canRecord }: { canRecord: boolean }) {
  const { data: cfg } = useQuery<{ today: string; groups: Group[] }>({ queryKey: ['check-config'], queryFn: () => api.get('/checks/config').then(r => r.data) })
  const [gid, setGid] = useState('')
  const [month, setMonth] = useState('')
  const groups = cfg?.groups.filter(g => g.isActive) ?? []
  const group = groups.find(g => g.id === gid) ?? groups[0]
  const ym = month || cfg?.today.slice(0, 7) || ''
  if (!cfg) return <p className="text-center text-gray-400 py-16">กำลังโหลด…</p>
  if (!group) return <p className="text-center text-gray-400 py-16">ยังไม่มี checklist — ผู้ดูแลระบบสร้างได้ที่แท็บ "จัดการรายการ"</p>
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select value={group.id} onChange={e => setGid(e.target.value)} className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm bg-white" aria-label="Group">
          {groups.map(g => <option key={g.id} value={g.id}>{g.name} ({FREQ_LABEL[g.frequency]})</option>)}
        </select>
        <div className="flex items-center gap-1">
          <button onClick={() => setMonth(shiftMonth(ym, -1))} className="p-1.5 border border-gray-200 rounded-lg hover:bg-gray-50" aria-label="เดือนก่อน"><ChevronLeft size={15}/></button>
          <input type="month" value={ym} onChange={e => e.target.value && setMonth(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1 text-sm"/>
          <button onClick={() => setMonth(shiftMonth(ym, 1))} className="p-1.5 border border-gray-200 rounded-lg hover:bg-gray-50" aria-label="เดือนถัดไป"><ChevronRight size={15}/></button>
        </div>
        {!canRecord && <span className="text-xs text-gray-400">ดูอย่างเดียว</span>}
      </div>
      <CheckGrid groupId={group.id} month={ym} canRecord={canRecord}/>
    </div>
  )
}

export default function DailyCheckPage({ isAdmin, role, isSuperAdmin, activeCompanyId }: { isAdmin: boolean; role: string; isSuperAdmin: boolean; activeCompanyId: string | null }) {
  const [tab, setTab] = useState<'record' | 'dashboard' | 'manage'>('record')
  const tabs = [
    { k: 'record' as const, label: 'บันทึกผล' },
    { k: 'dashboard' as const, label: 'Dashboard' },
    ...(isAdmin ? [{ k: 'manage' as const, label: 'จัดการรายการ' }] : []),
  ]
  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-[1400px] mx-auto">
      <div>
        <h1 className="text-xl font-bold text-gray-900">System Check</h1>
        <p className="text-sm text-gray-500">ตรวจสอบระบบ IT ประจำวัน/สัปดาห์/เดือน พร้อม Dashboard ภาพรวม</p>
      </div>
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto" role="tablist">
        {tabs.map(t => (
          <button key={t.k} role="tab" aria-selected={tab === t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px ${tab === t.k ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>{t.label}</button>
        ))}
      </div>
      {tab === 'record' && <RecordTab canRecord={role !== 'VIEWER'}/>}
      {tab === 'dashboard' && <CheckDashboard isSuperAdmin={isSuperAdmin} activeCompanyId={activeCompanyId}/>}
      {tab === 'manage' && isAdmin && <ManageItems/>}
    </div>
  )
}
